import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { KeyboardDatePicker as MuiKeyboardDatePicker, KeyboardDateTimePicker as MuiKeyboardDateTimePicker } from '@material-ui/pickers';
import cn from 'classnames';
import { isSameDay } from 'date-fns';
import { ColorCalendar } from '../../icons';
import { Spinner } from '../Spinner';
import styles from './KeyboardDatePicker.module.scss';
import { useKeyboardDatePickerStyles } from './KeyboardDatePicker.styles';
const defaultDayHighlightSkin = 'standard';
const DEFAULT_DATE_FORMAT = 'PP';
const DEFAULT_DATETIME_FORMAT = 'MMM d, yyyy hh:mm a';
export function KeyboardDatePicker(props) {
    // IMPORTANT: Extract highlightDay BEFORE destructuring to avoid it being spread into rest
    const highlightDay = 'highlightDay' in props ? props.highlightDay : undefined;
    const { loading, disabled, disableErrors, size = 'medium', autoOk = true, inputVariant = 'outlined', variant = 'inline', onChange, helperTextReserveSpace, withTime = false, format: formatProp, highlightDay: _, testId } = props, rest = _object_without_properties(props, [
        "loading",
        "disabled",
        "disableErrors",
        "size",
        "autoOk",
        "inputVariant",
        "variant",
        "onChange",
        "helperTextReserveSpace",
        "withTime",
        "format",
        "highlightDay",
        "testId"
    ]);
    // IMPORTANT: Compute format AFTER destructuring so withTime is available
    const format = formatProp !== null && formatProp !== void 0 ? formatProp : withTime ? DEFAULT_DATETIME_FORMAT : DEFAULT_DATE_FORMAT;
    const isDisabled = disabled || loading;
    const overrides = useKeyboardDatePickerStyles({
        isDisabled
    });
    const [open, setOpen] = useState(false);
    const disableErrorsProps = disableErrors || open ? {
        error: false,
        helperText: null
    } : {};
    const handleOpen = ()=>{
        setOpen(true);
    };
    const handleClose = ()=>{
        setOpen(false);
    };
    // Date-time mode: use MuiKeyboardDateTimePicker, no normalization
    if (withTime) {
        return /*#__PURE__*/ _jsx(MuiKeyboardDateTimePicker, _object_spread_props(_object_spread({}, rest, disableErrorsProps), {
            "data-testid": testId,
            disabled: isDisabled,
            keyboardIcon: loading ? /*#__PURE__*/ _jsx(Spinner, {
                size: 24,
                testId: testId && "".concat(testId, "-loading")
            }) : /*#__PURE__*/ _jsx(ColorCalendar, {
                style: isDisabled ? {
                    opacity: 0.4
                } : undefined
            }),
            autoOk: autoOk,
            format: format,
            inputVariant: inputVariant,
            variant: variant,
            focused: open,
            size: size,
            inputProps: _object_spread({
                'data-testid': testId && "".concat(testId, "-input")
            }, props.inputProps),
            open: open,
            onOpen: handleOpen,
            onClose: handleClose,
            KeyboardButtonProps: _object_spread({
                'data-testid': testId && "".concat(testId, "-btn")
            }, props.KeyboardButtonProps),
            leftArrowButtonProps: _object_spread({
                'data-testid': testId && "".concat(testId, "-prev-month")
            }, props.leftArrowButtonProps),
            rightArrowButtonProps: _object_spread({
                'data-testid': testId && "".concat(testId, "-next-month")
            }, props.rightArrowButtonProps),
            FormHelperTextProps: _object_spread({
                ['data-testid']: testId && "".concat(testId, "-helper-text"),
                classes: {
                    contained: cn(overrides.helperText, {
                        positionFixed: !helperTextReserveSpace
                    })
                }
            }, props.FormHelperTextProps),
            InputLabelProps: {
                classes: {
                    root: overrides.label
                }
            },
            InputProps: _object_spread({
                disabled: isDisabled,
                readOnly: true,
                classes: {
                    root: overrides.input,
                    adornedEnd: overrides.adornedEnd,
                    input: cn(overrides.inputInput, overrides["inputInput_".concat(size)])
                }
            }, props.InputProps),
            onChange: (date, value)=>onChange(date, value)
        }));
    }
    // Date-only mode: use MuiKeyboardDatePicker with normalization and highlightDay
    return /*#__PURE__*/ _jsx(MuiKeyboardDatePicker, _object_spread_props(_object_spread({}, rest, disableErrorsProps), {
        "data-testid": testId,
        disabled: isDisabled,
        keyboardIcon: loading ? /*#__PURE__*/ _jsx(Spinner, {
            size: 24,
            testId: testId && "".concat(testId, "-loading")
        }) : /*#__PURE__*/ _jsx(ColorCalendar, {
            style: isDisabled ? {
                opacity: 0.4
            } : undefined
        }),
        autoOk: autoOk,
        format: format,
        inputVariant: inputVariant,
        variant: variant,
        focused: open,
        size: size,
        inputProps: _object_spread({
            'data-testid': testId && "".concat(testId, "-input")
        }, props.inputProps),
        open: open,
        onOpen: handleOpen,
        onClose: handleClose,
        KeyboardButtonProps: _object_spread({
            'data-testid': testId && "".concat(testId, "-btn")
        }, props.KeyboardButtonProps),
        leftArrowButtonProps: _object_spread({
            'data-testid': testId && "".concat(testId, "-prev-month")
        }, props.leftArrowButtonProps),
        rightArrowButtonProps: _object_spread({
            'data-testid': testId && "".concat(testId, "-next-month")
        }, props.rightArrowButtonProps),
        FormHelperTextProps: _object_spread({
            ['data-testid']: testId && "".concat(testId, "-helper-text"),
            classes: {
                contained: cn(overrides.helperText, {
                    positionFixed: !helperTextReserveSpace
                })
            }
        }, props.FormHelperTextProps),
        InputLabelProps: {
            classes: {
                root: overrides.label
            }
        },
        InputProps: _object_spread({
            disabled: isDisabled,
            readOnly: true,
            classes: {
                root: overrides.input,
                adornedEnd: overrides.adornedEnd,
                input: cn(overrides.inputInput, overrides["inputInput_".concat(size)])
            }
        }, props.InputProps),
        renderDay: (day, selectedDate, dayInCurrentMonth, dayComponent)=>{
            const highlighted = day && dayInCurrentMonth && highlightDay ? highlightDay(day) : false;
            const dayTestId = day && testId ? "".concat(testId, "-day-").concat(day.getMonth() + 1, "-").concat(day.getDate()) : undefined;
            return /*#__PURE__*/ _jsxs("div", {
                className: styles.dayWrapper,
                "data-testid": dayTestId,
                children: [
                    !!highlighted && !(day && selectedDate && isSameDay(day, selectedDate)) && /*#__PURE__*/ _jsx("div", {
                        className: cn(styles.dayHighlight, styles[typeof highlighted === 'string' ? highlighted : defaultDayHighlightSkin]),
                        "data-testid": dayTestId && "".concat(dayTestId, "-highlighted")
                    }),
                    dayComponent
                ]
            });
        },
        onChange: (date, value)=>{
            const isValid = date instanceof Date && !isNaN(date.getTime());
            // Normalize to start of day for date-only mode
            onChange(isValid ? new Date(date.setHours(0, 0, 0, 0)) : null, value);
        }
    }));
}
